import { useRef, useState, useMemo } from "react";
import { createRoot } from "react-dom/client";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@utils/cn.ts";
import {
  ChevronDown,
  Loader2,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  X,
} from "lucide-react";
import useRosterConfig from "@/hooks/useRosterConfig";
import useStore from "@/store/store";

import type { ValidationPhase } from "@/hooks/useRosterValidation";

type ValidateListType = {
  violations: string[];
  detailed_violations: Array<{
    nurse_id?: string;
    day?: number;
    start_day?: number;
    end_day?: number;
    type: string;
    [key: string]: unknown;
  }>;
};

/** 위반 그룹 바가 들어 있는 셀 셸 — `padding_hover_bg_horizon` 제거 후에도 탐색되도록 */
function closestRosterShiftCellShell(
  el: HTMLElement | null
): HTMLElement | null {
  if (!el) return null;
  return (
    (el.closest("[data-roster-shift-cell]") as HTMLElement | null) ??
    (el.closest(".padding_hover_bg_horizon") as HTMLElement | null)
  );
}

function ViolationsFloatingMenu({
  validateList,
  isValidating,
  phase = "idle",
  hideManpowerToggle: _hideManpowerToggle = false,
  isOpen,
  onClose,
}: {
  validateList: ValidateListType | null;
  isValidating: boolean;
  phase?: ValidationPhase;
  hideManpowerToggle?: boolean;
  isOpen: boolean;
  onClose: () => void;
}) {
  // hideManpowerToggle은 향후 사용 예정 (현재는 사용하지 않음)
  void _hideManpowerToggle;

  const { data: serverConfig } = useRosterConfig();

  // 위반사항 표시 토글 상태
  const { showManpowerViolations, showConfigViolations } = useStore();

  // 하이라이트 제거 타이머 관리
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // 디바운싱을 위한 마지막 클릭 시간
  const lastClickTimeRef = useRef<number>(0);

  const clearHighlightEffects = () => {
    document.querySelectorAll(".highlight-violation-cell").forEach((cell) => {
      cell.classList.remove("highlight-violation-cell");
    });
    document.querySelectorAll(".violation-group-highlight").forEach((bar) => {
      bar.classList.remove("violation-group-highlight");
    });
    document.querySelectorAll(".violation-row-indicator").forEach((el) => {
      el.remove();
    });
  };

  const highlightViolationGroupBar = (
    targetCells: HTMLElement[],
    violationType: string
  ) => {
    if (targetCells.length === 0) return;
    // 각 셀의 해당 위반 타입 바를 하이라이트
    targetCells.forEach((cell) => {
      const wrapper = closestRosterShiftCellShell(cell);
      const violationBar = wrapper?.querySelector(
        `.violation-group-bar[data-violation-type="${violationType}"]`
      ) as HTMLElement | null;
      if (violationBar) {
        violationBar.classList.add("violation-group-highlight");
      }
    });
  };

  // Render 형식으로 함 ...
  const ViolationIndicator = () => {
    return (
      // row indicator 앵커(centerX) 기준으로 중앙 상단에 고정
      <div className="absolute size-[22px] -top-4 -left-6 ">
        <div className="absolute inset-0 rounded-full z-1 bg-gradient-to-r from-red-600 to-red-400 text-white border-2 border-white flex items-center justify-center text-[14px] font-extrabold leading-none box-border">
          !
        </div>
        <div className="absolute inset-0 rounded-full bg-red-500/55 scale-125 -z-0 animate-ping" />
      </div>
    );
  };

  const renderRowIndicators = (
    targetCells: HTMLElement[],
    violationDetail?: ValidateListType["detailed_violations"][number]
  ) => {
    const cellsByRow = new Map<HTMLElement, HTMLElement[]>();

    targetCells.forEach((cell) => {
      // 스케줄 행은 클래스 대신 data-roster-schedule-row로 고정 (레이아웃 리팩터 시에도 느낌표 앵커 유지)
      const rowWrap =
        (cell.closest("[data-roster-schedule-row]") as HTMLElement | null) ??
        (cell.closest(".rt_date_flexWrap") as HTMLElement | null) ??
        (cell.closest('[class*="rt_date_flexWrap"]') as HTMLElement | null) ??
        closestRosterShiftCellShell(cell);
      if (!rowWrap) return;

      const rowCells = cellsByRow.get(rowWrap) ?? [];
      rowCells.push(cell);
      cellsByRow.set(rowWrap, rowCells);
    });

    cellsByRow.forEach((rowCells, rowWrap) => {
      if (rowCells.length === 0) return;

      // 연속 위반의 경우 범위 전체를 계산
      let startDay: number | null = null;
      let endDay: number | null = null;

      if (violationDetail) {
        const isContinuousViolation =
          violationDetail.type === "three_seq_nig" ||
          violationDetail.type === "max_conseq_work" ||
          violationDetail.type === "banned_day_after_eve" ||
          violationDetail.type === "two_offs_after_three_nig" ||
          violationDetail.type === "two_offs_after_two_nig";

        if (isContinuousViolation) {
          // 연속 위반: 범위 정보 사용
          if (
            violationDetail.start_day !== undefined &&
            violationDetail.end_day !== undefined
          ) {
            startDay = violationDetail.start_day + 1; // 1-based
            endDay = violationDetail.end_day + 1;
          } else if (
            violationDetail.day !== undefined &&
            violationDetail.next_day !== undefined &&
            typeof violationDetail.next_day === "number"
          ) {
            // banned_day_after_eve
            startDay = violationDetail.day + 1;
            endDay = violationDetail.next_day + 1;
          } else if (
            violationDetail.start_day !== undefined &&
            violationDetail.off_end_day !== undefined &&
            typeof violationDetail.off_end_day === "number"
          ) {
            // two_offs_after_*
            startDay = violationDetail.start_day + 1;
            endDay = violationDetail.off_end_day + 1;
          }
        }
      }

      // 범위가 있으면 범위 전체의 셀을 찾아서 중앙 계산
      if (startDay !== null && endDay !== null && violationDetail?.nurse_id) {
        const startCell = document.querySelector(
          `[data-nurse-id="${violationDetail.nurse_id}"][data-day="${startDay}"]`
        ) as HTMLElement | null;
        const endCell = document.querySelector(
          `[data-nurse-id="${violationDetail.nurse_id}"][data-day="${endDay}"]`
        ) as HTMLElement | null;

        if (startCell && endCell) {
          const rowRect = rowWrap.getBoundingClientRect();
          const startRect = startCell.getBoundingClientRect();
          const endRect = endCell.getBoundingClientRect();

          const centerX = (startRect.left + endRect.right) / 2 - rowRect.left;

          const indicator = document.createElement("div");
          indicator.className = "violation-row-indicator";
          indicator.style.left = `${centerX}px`;
          indicator.style.top = "8px";
          indicator.style.position = "absolute";
          indicator.style.transform = "translateX(-50%)";
          // indicator.style.zIndex = "10";
          indicator.style.pointerEvents = "none";
          rowWrap.appendChild(indicator);

          // React 컴포넌트로 렌더링
          const root = createRoot(indicator);
          root.render(<ViolationIndicator />);
          return;
        }
      }

      // 범위가 없거나 찾지 못한 경우: 기존 로직 (첫 번째와 마지막 셀의 중앙)
      const sortedCells = [...rowCells].sort((a, b) => {
        const dayA = Number(a.getAttribute("data-day") || "0");
        const dayB = Number(b.getAttribute("data-day") || "0");
        return dayA - dayB;
      });

      const firstCell = sortedCells[0];
      const lastCell = sortedCells[sortedCells.length - 1];
      const rowRect = rowWrap.getBoundingClientRect();
      const firstRect = firstCell.getBoundingClientRect();
      const lastRect = lastCell.getBoundingClientRect();

      const centerX = (firstRect.left + lastRect.right) / 2 - rowRect.left;

      const indicator = document.createElement("div");
      indicator.className = "violation-row-indicator";
      indicator.style.left = `${centerX}px`;
      indicator.style.top = "8px";
      indicator.style.position = "absolute";
      indicator.style.transform = "translateX(-50%)";
      // indicator.style.zIndex = "10";
      indicator.style.pointerEvents = "none";
      rowWrap.appendChild(indicator);

      // React 컴포넌트로 렌더링
      const root = createRoot(indicator);
      root.render(<ViolationIndicator />);
    });
  };

  // 필터링된 위반사항 리스트 계산
  const filteredViolations = useMemo(() => {
    if (!validateList?.violations || !validateList?.detailed_violations) {
      return { violations: [], detailed_violations: [] };
    }

    const filtered: string[] = [];
    const filteredDetails: typeof validateList.detailed_violations = [];

    validateList.violations.forEach((text: string, index: number) => {
      const detail = validateList.detailed_violations[index];
      if (!detail) return;

      // 인력 위반(미달/초과) 필터링
      if (
        detail.type === "manpower_shortage" ||
        detail.type === "manpower_excess"
      ) {
        if (showManpowerViolations) {
          filtered.push(text);
          filteredDetails.push(detail);
        }
        return;
      }

      // 설정 위반 필터링
      if (showConfigViolations) {
        filtered.push(text);
        filteredDetails.push(detail);
      }
    });

    return {
      violations: filtered,
      detailed_violations: filteredDetails,
    };
  }, [validateList, showManpowerViolations, showConfigViolations]);

  // 분류별 건수 계산
  const violationCounts = useMemo(() => {
    if (!validateList?.detailed_violations) {
      return { total: 0, manpower: 0, config: 0 };
    }

    let manpower = 0;
    let config = 0;

    validateList.detailed_violations.forEach((detail) => {
      if (
        detail.type === "manpower_shortage" ||
        detail.type === "manpower_excess"
      ) {
        manpower++;
      } else {
        config++;
      }
    });

    return {
      total: validateList.violations.length,
      manpower,
      config,
    };
  }, [validateList]);

  // 상태 결정 (필터링된 리스트 기준)
  const filteredCount = filteredViolations.violations.length;
  const hasResult = phase === "done" || validateList !== null;
  const isViolation = hasResult && filteredCount > 0;
  const isSafe = hasResult && filteredCount === 0;
  const isNeutral = !hasResult;

  // 섹션별 독립적인 펼침/접힘 상태 관리
  const [isMoreConf, setIsMoreConf] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // 배경색 및 구분선 색상 결정
  const containerBgColor =
    isValidating || isNeutral
      ? "bg-zinc-100"
      : isViolation
        ? "bg-[#FFEAE9]"
        : "bg-[#E2F1E0]";
  const dividerColor =
    isValidating || isNeutral
      ? "divide-gray-200"
      : isViolation
        ? "divide-[#ff6c61]/30"
        : "divide-[#42af3a]/30";

  // 텍스트/아이콘 색상 결정
  const statusColor =
    isValidating || isNeutral
      ? "text-gray-400"
      : isViolation
        ? "text-[#ff6c61]"
        : "text-[#42af3a]";

  // 호버 배경색 결정
  const hoverColor =
    isValidating || isNeutral
      ? "hover:bg-gray-200/50"
      : isViolation
        ? "hover:bg-[#ff6c61]/10"
        : "hover:bg-[#42af3a]/10";

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      className="fixed bottom-5 z-50 right-9 w-[300px]  shadow-2xl rounded-lg overflow-hidden"
    >
      <div
        className={cn(
          "rounded-sm divide-y flex flex-col",
          containerBgColor,
          dividerColor
        )}
      >
        {/* --- 위반사항 섹션 --- */}
        <section className="sec_notice_section violationsContainer max-h-[300px]">
          <div
            className={cn(
              "flex items-center justify-between px-3 rounded-sm transition-colors py-3",
              "cursor-default"
            )}
          >
            {/* 좌측: 아이콘 + 제목 + 카운트 */}
            <div className="flex items-center justify-between gap-1 w-full">
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1">
                  {isValidating ? (
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  ) : isViolation ? (
                    <ShieldAlert className={cn("w-5 h-5", statusColor)} />
                  ) : isSafe ? (
                    <ShieldCheck className={cn("w-5 h-5", statusColor)} />
                  ) : (
                    <Shield className={cn("w-5 h-5", statusColor)} />
                  )}
                  <h3 className={cn("font-semibold text-sm", statusColor)}>
                    {isValidating
                      ? "검증 중..."
                      : isNeutral
                        ? "검증 대기"
                        : "위반사항"}
                  </h3>
                </TooltipTrigger>
                <TooltipContent className="bg-[#444444]">
                  <p>법정 규정 및 병원 내규에 위반되는 사항</p>
                </TooltipContent>
              </Tooltip>

              <div className={cn("mr-auto", isValidating && "invisible")}>
                {isViolation && validateList ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="bg-[#ff6c61] text-white px-1.5 py-0.5 rounded-sm text-xs font-medium cursor-help">
                        {filteredCount}건
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="bg-[#444444]">
                      <p>
                        전체: {violationCounts.total}건 (최소인원:{" "}
                        {violationCounts.manpower}건, 설정:{" "}
                        {violationCounts.config}
                        건)
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : isSafe ? (
                  <div className="bg-[#42AF3A] text-white px-1.5 py-0.5 rounded-sm text-xs font-medium">
                    없음
                  </div>
                ) : (
                  <div className="bg-gray-400 text-white px-1.5 py-0.5 rounded-sm text-xs font-medium">
                    -
                  </div>
                )}
              </div>

              {/* 우측: 닫기 버튼 (X) */}
              {!isValidating && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="flex items-center justify-center p-1 hover:bg-gray-200/50 rounded transition-colors"
                >
                  <X className={cn("w-5 h-5", statusColor)} />
                </button>
              )}
            </div>
          </div>

          {/* 위반사항 리스트 - 필터링된 것만 표시 */}
          {!isValidating && validateList && isViolation && (
            <div className="sec_notice_listContainer">
              {filteredViolations.violations.map(
                (text: string, index: number) => {
                  const violationDetail =
                    filteredViolations.detailed_violations?.[index];

                  const handleClick = () => {
                    if (!violationDetail || !validateList) return;

                    // 디바운싱: 300ms 이내 연속 클릭 무시
                    const now = Date.now();
                    if (now - lastClickTimeRef.current < 300) {
                      return;
                    }
                    lastClickTimeRef.current = now;

                    // 이전 타이머가 있으면 취소
                    if (highlightTimeoutRef.current) {
                      clearTimeout(highlightTimeoutRef.current);
                      highlightTimeoutRef.current = null;
                    }
                    clearHighlightEffects();

                    // 인력 위반(미달/초과): 해당 날짜의 해당 shift 타입(D/E/N)인 셀들만 하이라이트
                    if (
                      (violationDetail.type === "manpower_shortage" ||
                        violationDetail.type === "manpower_excess") &&
                      violationDetail.day !== undefined &&
                      violationDetail.shift
                    ) {
                      const targetDay = violationDetail.day + 1; // 1-based
                      const targetShift = violationDetail.shift as string; // "D", "E", "N"
                      // 해당 날짜의 모든 셀 찾기
                      const allCells = document.querySelectorAll(
                        `[data-day="${targetDay}"]`
                      ) as NodeListOf<HTMLElement>;

                      // 해당 shift 타입인 셀들만 필터링
                      // 실제 UI 에러 셀(data-is-error="true")만 대상으로 하여
                      // 프리셉티 포함/미포함 설정과 동일하게 스크롤/하이라이트 동작
                      const targetCells = Array.from(allCells).filter(
                        (cell) => {
                          const defaultShift =
                            cell.getAttribute("data-default-shift");
                          const shiftGb = cell.getAttribute("data-shift-gb");
                          const isErrorCell =
                            cell.getAttribute("data-is-error") === "true";

                          if (!isErrorCell) {
                            return false;
                          }

                          // shift 타입 체크
                          let matchesShift = false;
                          if (defaultShift === targetShift) {
                            matchesShift = true;
                          } else if (!defaultShift || defaultShift === "") {
                            if (targetShift === "D" && shiftGb === "데이") {
                              matchesShift = true;
                            } else if (
                              targetShift === "E" &&
                              shiftGb === "이브닝"
                            ) {
                              matchesShift = true;
                            } else if (
                              targetShift === "N" &&
                              shiftGb === "나이트"
                            ) {
                              matchesShift = true;
                            }
                          }

                          if (!matchesShift) {
                            return false;
                          }

                          return true;
                        }
                      );

                      if (targetCells.length > 0) {
                        // 첫 번째 셀로 스크롤
                        targetCells[0].scrollIntoView({
                          behavior: "smooth",
                          block: "center",
                        });

                        // 인력 위반 바 하이라이트
                        targetCells.forEach((cell) => {
                          const wrapper = closestRosterShiftCellShell(cell);
                          const violationBar = wrapper?.querySelector(
                            '.violation-group-bar[data-violation-type="manpower_shortage"], .violation-group-bar[data-violation-type="manpower_excess"]'
                          ) as HTMLElement | null;
                          if (violationBar) {
                            violationBar.classList.add(
                              "violation-group-highlight"
                            );
                          }
                        });
                        renderRowIndicators(targetCells, violationDetail);

                        // 2초 후 제거 (타이머 ID 저장)
                        highlightTimeoutRef.current = setTimeout(() => {
                          clearHighlightEffects();
                          highlightTimeoutRef.current = null;
                        }, 2000);
                      }
                      return;
                    }

                    // 간호사별 위반: nurse_id 사용
                    const nurseId = violationDetail.nurse_id;
                    if (!nurseId) return;

                    let targetCells: HTMLElement[] = [];
                    let scrollTarget: HTMLElement | null = null;

                    // 위반 타입에 따라 정확한 셀들만 찾기
                    switch (violationDetail.type) {
                      case "three_seq_nig":
                      case "max_conseq_work": {
                        // 범위가 있는 위반: start_day ~ end_day 전체 하이라이트
                        if (
                          violationDetail.start_day !== undefined &&
                          violationDetail.end_day !== undefined
                        ) {
                          const startDay = violationDetail.start_day + 1;
                          const endDay = violationDetail.end_day + 1;
                          const violationType = violationDetail.type;

                          for (let day = startDay; day <= endDay; day++) {
                            // 연속 위반이므로 data-is-error 필터링 제거
                            const cell = document.querySelector(
                              `[data-nurse-id="${nurseId}"][data-day="${day}"]`
                            ) as HTMLElement;
                            if (cell) {
                              // 해당 위반 타입의 바가 있는 셀만 추가
                              const wrapper = closestRosterShiftCellShell(cell);
                              const hasViolationBar = wrapper?.querySelector(
                                `.violation-group-bar[data-violation-type="${violationType}"]`
                              );
                              if (hasViolationBar) {
                                targetCells.push(cell);
                                if (day === startDay) scrollTarget = cell;
                              }
                            }
                          }
                        }
                        break;
                      }

                      case "banned_day_after_eve": {
                        // day와 next_day 셀 모두 하이라이트 (연속 위반이므로)
                        const nextDayValue = violationDetail.next_day;
                        if (
                          violationDetail.day !== undefined &&
                          nextDayValue !== undefined &&
                          typeof nextDayValue === "number"
                        ) {
                          const day = violationDetail.day + 1;
                          const nextDay = nextDayValue + 1;

                          // 연속 위반이므로 data-is-error 필터링 제거
                          // 해당 날짜의 셀을 찾고, 해당 위반 타입의 바가 있는지 확인
                          const cell1 = document.querySelector(
                            `[data-nurse-id="${nurseId}"][data-day="${day}"]`
                          ) as HTMLElement;
                          const cell2 = document.querySelector(
                            `[data-nurse-id="${nurseId}"][data-day="${nextDay}"]`
                          ) as HTMLElement;

                          // 해당 위반 타입의 바가 있는 셀만 추가
                          if (cell1) {
                            const wrapper1 = closestRosterShiftCellShell(cell1);
                            const hasViolationBar1 = wrapper1?.querySelector(
                              `.violation-group-bar[data-violation-type="banned_day_after_eve"]`
                            );
                            if (hasViolationBar1) {
                              targetCells.push(cell1);
                              if (!scrollTarget) scrollTarget = cell1;
                            }
                          }
                          if (cell2) {
                            const wrapper2 = closestRosterShiftCellShell(cell2);
                            const hasViolationBar2 = wrapper2?.querySelector(
                              `.violation-group-bar[data-violation-type="banned_day_after_eve"]`
                            );
                            if (hasViolationBar2) {
                              targetCells.push(cell2);
                            }
                          }
                        }
                        break;
                      }

                      case "two_offs_after_three_nig":
                      case "two_offs_after_two_nig": {
                        // start_day ~ off_end_day 범위 전체 하이라이트
                        const offEndDayValue = violationDetail.off_end_day;
                        if (
                          violationDetail.start_day !== undefined &&
                          offEndDayValue !== undefined &&
                          typeof offEndDayValue === "number"
                        ) {
                          const startDay = violationDetail.start_day + 1;
                          const endDay = offEndDayValue + 1;
                          const violationType = violationDetail.type;

                          for (let day = startDay; day <= endDay; day++) {
                            // 연속 위반이므로 data-is-error 필터링 제거
                            const cell = document.querySelector(
                              `[data-nurse-id="${nurseId}"][data-day="${day}"]`
                            ) as HTMLElement;
                            if (cell) {
                              // 해당 위반 타입의 바가 있는 셀만 추가
                              const wrapper = closestRosterShiftCellShell(cell);
                              const hasViolationBar = wrapper?.querySelector(
                                `.violation-group-bar[data-violation-type="${violationType}"]`
                              );
                              if (hasViolationBar) {
                                targetCells.push(cell);
                                if (day === startDay) scrollTarget = cell;
                              }
                            }
                          }
                        }
                        break;
                      }

                      case "max_nig_per_month": {
                        // 해당 간호사의 N 근무 중 실제 UI 에러 셀만 대상
                        const allCells = document.querySelectorAll(
                          `[data-nurse-id="${nurseId}"]`
                        ) as NodeListOf<HTMLElement>;

                        targetCells = Array.from(allCells).filter((cell) => {
                          const defaultShift =
                            cell.getAttribute("data-default-shift");
                          const shiftGb = cell.getAttribute("data-shift-gb");
                          const isErrorCell =
                            cell.getAttribute("data-is-error") === "true";
                          return (
                            isErrorCell &&
                            (defaultShift === "N" || shiftGb === "나이트")
                          );
                        });

                        if (targetCells.length > 0) {
                          scrollTarget = targetCells[0];
                        }
                        break;
                      }

                      default: {
                        // 기타 위반: 실제 UI 에러 셀만 대상으로 동작
                        if (violationDetail.day !== undefined) {
                          const day = violationDetail.day + 1;
                          const cell = document.querySelector(
                            `[data-nurse-id="${nurseId}"][data-day="${day}"][data-is-error="true"]`
                          ) as HTMLElement;
                          if (cell) {
                            targetCells.push(cell);
                            scrollTarget = cell;
                          }
                        } else {
                          // 첫 번째 UI 에러 셀 찾기
                          const firstCell = document.querySelector(
                            `[data-nurse-id="${nurseId}"][data-is-error="true"]`
                          ) as HTMLElement;
                          if (firstCell) {
                            targetCells.push(firstCell);
                            scrollTarget = firstCell;
                          }
                        }
                        break;
                      }
                    }

                    if (targetCells.length > 0 && scrollTarget) {
                      scrollTarget.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });

                      const isContinuousViolation =
                        violationDetail.type === "three_seq_nig" ||
                        violationDetail.type === "max_conseq_work" ||
                        violationDetail.type === "banned_day_after_eve" ||
                        violationDetail.type === "two_offs_after_three_nig" ||
                        violationDetail.type === "two_offs_after_two_nig";

                      if (isContinuousViolation) {
                        // 연속 위반은 셀이 아닌 연속 위반 래퍼 바를 강조
                        highlightViolationGroupBar(
                          targetCells,
                          violationDetail.type
                        );
                      } else {
                        // 그 외 위반은 셀 하이라이트 유지
                        targetCells.forEach((cell) => {
                          cell.classList.add("highlight-violation-cell");
                        });
                      }
                      renderRowIndicators(targetCells, violationDetail);

                      // 2초 후 제거 (타이머 ID 저장)
                      highlightTimeoutRef.current = setTimeout(() => {
                        clearHighlightEffects();
                        highlightTimeoutRef.current = null;
                      }, 2000);
                    }
                  };

                  return (
                    <div
                      key={index}
                      className="text-xs grid grid-cols-[50px_1fr] hover:bg-zinc-100 cursor-pointer [&>span]:inline-block [&>span]:p-1.5 [&>span]:border-b [&>span:first-child]:font-semibold [&>span:first-child]:border-r transition-colors"
                      dangerouslySetInnerHTML={{ __html: text }}
                      onClick={handleClick}
                    />
                  );
                }
              )}
            </div>
          )}
        </section>

        {/* --- 근무표 기본설정 섹션 --- */}
        <section className="sec_notice_section rulesContainer">
          <div
            className={cn(
              "flex items-center justify-between px-3 cursor-pointer rounded-sm transition-colors py-3",
              hoverColor
            )}
            onClick={() => setIsMoreConf((prev) => !prev)}
          >
            {/* 좌측: 아이콘 + 제목 */}
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1">
                  <SlidersHorizontal className={cn("w-5 h-5", statusColor)} />
                  <h3 className={cn("font-semibold text-sm", statusColor)}>
                    근무표 기본설정
                  </h3>
                </TooltipTrigger>
                <TooltipContent className="bg-[#444444]">
                  <p>근무표 기본설정</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* 우측: 더보기 버튼 */}
            <button
              type="button"
              className="flex items-center justify-center p-1"
            >
              <ChevronDown
                className={cn(
                  "w-5 h-5 transition-transform duration-200",
                  statusColor,
                  isMoreConf && "rotate-180"
                )}
              />
            </button>
          </div>

          {/* 기본설정 리스트 */}
          {isMoreConf && (
            <div className="sec_notice_listContainer py-1">
              <h4 className="sec_notice_listTitle font-semibold text-sm! mb-1">
                법정기준 설정
              </h4>
              <ul>
                <li className="text-xs text-zinc-600 flex gap-1">
                  <span>
                    연속 근무일수 최대 {serverConfig?.max_conseq_work ?? "-"}일
                    이하
                  </span>
                  <span className="text-zinc-900">
                    {serverConfig?.max_conseq_work ? "적용" : "미적용"}
                  </span>
                </li>
                <li className="text-xs text-zinc-600 flex gap-1">
                  ND/ED/NE 근무 패턴 불가능{" "}
                  <span className="text-zinc-900">
                    {serverConfig?.banned_day_after_eve ? "적용" : "미적용"}
                  </span>
                </li>
                <li className="text-xs text-zinc-600 flex gap-1">
                  나이트 연속 근무 최대 3일 이하{" "}
                  <span className="text-zinc-900">
                    {serverConfig?.three_seq_nig ? "적용" : "미적용"}
                  </span>
                </li>
                {serverConfig?.three_seq_nig ? (
                  <li className="text-xs text-zinc-600 flex gap-1">
                    나이트 <span className="text-sig font-semibold">3회</span>{" "}
                    이후 OFF 2회{" "}
                    <span className="text-zinc-900">
                      {serverConfig?.two_offs_after_three_nig
                        ? "적용"
                        : "미적용"}
                    </span>
                  </li>
                ) : (
                  <li className="text-xs text-zinc-600 flex gap-1">
                    나이트 <span className="text-sig font-semibold">2회</span>{" "}
                    이후 OFF 2회{" "}
                    <span className="text-zinc-900">
                      {serverConfig?.two_offs_after_two_nig ? "적용" : "미적용"}
                    </span>
                  </li>
                )}
                <li className="text-xs text-zinc-600 flex gap-1">
                  월 나이트 근무 횟수{" "}
                  <span className="text-zinc-900">
                    {serverConfig?.max_nig_per_month &&
                    serverConfig.max_nig_per_month > 0
                      ? `${serverConfig.max_nig_per_month}회 적용`
                      : "미적용"}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default ViolationsFloatingMenu;
